
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useRevealAnimation } from "@/hooks/use-reveal-animation";

interface Article {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  image: string;
  slug: string;
}

interface FeaturedArticlesProps {
  articles: Article[];
}

const FeaturedArticles = ({ articles }: FeaturedArticlesProps) => {
  return (
    <section className="py-10 md:py-16 bg-sage-50">
      <div className="container-custom">
        <div className="flex justify-between items-center mb-6 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-bold">Latest Articles</h2>
          <Link
            to="/blog"
            className="flex items-center text-primary hover:text-primary/80 transition-colors button-hover"
          >
            View all
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {articles.map((article, index) => {
            const { ref, isVisible } = useRevealAnimation<HTMLAnchorElement>();
            return (
              <Link 
                key={article.id} 
                to={article.slug} 
                className="group hover-card"
                ref={ref}
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `all 0.5s ease-out ${index * 0.1}s`,
                }}
              >
                <article className="bg-white rounded-lg overflow-hidden shadow-md transition-all hover:shadow-lg">
                  <div className="aspect-video overflow-hidden image-hover">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="text-sm text-primary mb-2">{article.category}</div>
                    <h3 className="text-lg md:text-xl font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-4 line-clamp-3">{article.excerpt}</p>
                    <div className="flex items-center text-primary">
                      <span>Read more</span>
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedArticles;
